import React, { useState, useEffect, useMemo } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const RegexTesterPage = () => {
  const [pattern, setPattern] = useState('');
  const [testString, setTestString] = useState('');
  const [flags, setFlags] = useState({
    global: true,
    ignoreCase: false,
    multiline: false,
    dotAll: false,
    unicode: false,
    sticky: false
  });
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(true);

  // Test regex and find matches
  const testRegex = useMemo(() => {
    if (!pattern || !testString) {
      setMatches([]);
      setError('');
      setIsValid(true);
      return;
    }

    try {
      const flagString = Object.entries(flags)
        .filter(([_, enabled]) => enabled)
        .map(([flag, _]) => {
          switch(flag) {
            case 'global': return 'g';
            case 'ignoreCase': return 'i';
            case 'multiline': return 'm';
            case 'dotAll': return 's';
            case 'unicode': return 'u';
            case 'sticky': return 'y';
            default: return '';
          }
        })
        .join('');

      const regex = new RegExp(pattern, flagString);
      setIsValid(true);
      setError('');

      if (flags.global) {
        const allMatches = [];
        let match;
        const globalRegex = new RegExp(pattern, flagString);
        
        while ((match = globalRegex.exec(testString)) !== null) {
          allMatches.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1),
            namedGroups: match.groups || {}
          });
          
          // Prevent infinite loop for zero-length matches
          if (match.index === globalRegex.lastIndex) {
            globalRegex.lastIndex++;
          }
        }
        setMatches(allMatches);
      } else {
        const match = regex.exec(testString);
        if (match) {
          setMatches([{
            match: match[0],
            index: match.index,
            groups: match.slice(1),
            namedGroups: match.groups || {}
          }]);
        } else {
          setMatches([]);
        }
      }
    } catch (err) {
      setIsValid(false);
      setError(err.message);
      setMatches([]);
    }
  }, [pattern, testString, flags]);

  // Common regex patterns
  const commonPatterns = [
    {
      name: 'Email',
      pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}',
      description: 'Matches email addresses',
      testString: 'Contact us at support@example.com or sales@company.org'
    },
    {
      name: 'URL',
      pattern: 'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)',
      description: 'Matches HTTP/HTTPS URLs',
      testString: 'Visit https://www.example.com or http://test.org for more info'
    },
    {
      name: 'Phone Number (US)',
      pattern: '\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})',
      description: 'Matches US phone numbers',
      testString: 'Call (555) 123-4567 or 555.987.6543 or 555-111-2222'
    },
    {
      name: 'IP Address',
      pattern: '\\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\b',
      description: 'Matches IPv4 addresses',
      testString: 'Server IPs: 192.168.1.1, 10.0.0.1, and 255.255.255.0'
    },
    {
      name: 'Date (MM/DD/YYYY)',
      pattern: '\\b(0?[1-9]|1[0-2])\\/(0?[1-9]|[12][0-9]|3[01])\\/(19|20)\\d{2}\\b',
      description: 'Matches dates in MM/DD/YYYY format',
      testString: 'Important dates: 12/25/2023, 1/1/2024, and 07/04/2024'
    },
    {
      name: 'Hex Color',
      pattern: '#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})\\b',
      description: 'Matches hex color codes',
      testString: 'Colors: #FF0000, #00ff00, #123, #ABCDEF'
    },
    {
      name: 'Credit Card',
      pattern: '\\b(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|3[0-9]{13}|6(?:011|5[0-9]{2})[0-9]{12})\\b',
      description: 'Matches major credit card numbers',
      testString: 'Cards: 4111111111111111, 5555555555554444'
    },
    {
      name: 'HTML Tags',
      pattern: '<\\/?[a-zA-Z][^>]*>',
      description: 'Matches HTML tags',
      testString: '<div class="container"><p>Hello <strong>world</strong>!</p></div>'
    }
  ];

  const handleFlagChange = (flagName) => {
    setFlags(prev => ({
      ...prev,
      [flagName]: !prev[flagName]
    }));
  };

  const loadPattern = (patternObj) => {
    setPattern(patternObj.pattern);
    setTestString(patternObj.testString);
    setFlags(prev => ({ ...prev, global: true }));
  };

  const highlightMatches = (text) => {
    if (!matches.length || !isValid) return text;

    let result = [];
    let lastIndex = 0;

    matches.forEach((match, i) => {
      // Add text before match
      if (match.index > lastIndex) {
        result.push(
          <span key={`before-${i}`}>
            {text.substring(lastIndex, match.index)}
          </span>
        );
      }

      // Add highlighted match
      result.push(
        <span
          key={`match-${i}`}
          style={{
            backgroundColor: '#ffeb3b',
            padding: '2px 4px',
            borderRadius: '3px',
            fontWeight: 'bold'
          }}
        >
          {match.match}
        </span>
      );

      lastIndex = match.index + match.match.length;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      result.push(
        <span key="after">
          {text.substring(lastIndex)}
        </span>
      );
    }

    return result;
  };

  return (
    <div>
      <SEO 
        title="Regex Tester - Test Regular Expressions Online" 
        description="Free online regex tester and debugger. Test regular expressions with real-time matching, syntax highlighting, and common pattern examples."
        keywords="regex tester, regular expression tester, regex debugger, regex validator, pattern matching, regex online"
      />
      
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Regex Tester</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Test and debug regular expressions with real-time matching and syntax highlighting.
        </p>
        
        <AdUnit />

        <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '20px' }}>
          {/* Input Section */}
          <div className="input-section">
            <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Pattern & Test String</h2>
            
            {/* Regex Pattern */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Regular Expression Pattern:
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  placeholder="Enter your regex pattern..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${isValid ? '#ddd' : '#f44336'}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box',
                    backgroundColor: isValid ? 'white' : '#ffebee'
                  }}
                />
                {!isValid && (
                  <div style={{ 
                    position: 'absolute', 
                    right: '10px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#f44336',
                    fontSize: '18px'
                  }}>
                    ⚠️
                  </div>
                )}
              </div>
              {error && (
                <div style={{ 
                  color: '#f44336', 
                  fontSize: '12px', 
                  marginTop: '5px',
                  padding: '8px',
                  backgroundColor: '#ffebee',
                  borderRadius: '4px'
                }}>
                  Error: {error}
                </div>
              )}
            </div>

            {/* Flags */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Flags:
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {Object.entries(flags).map(([flag, enabled]) => (
                  <label key={flag} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => handleFlagChange(flag)}
                      style={{ marginRight: '8px' }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      {flag === 'global' && 'Global (g)'}
                      {flag === 'ignoreCase' && 'Ignore Case (i)'}
                      {flag === 'multiline' && 'Multiline (m)'}
                      {flag === 'dotAll' && 'Dot All (s)'}
                      {flag === 'unicode' && 'Unicode (u)'}
                      {flag === 'sticky' && 'Sticky (y)'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Test String */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Test String:
              </label>
              <textarea
                value={testString}
                onChange={(e) => setTestString(e.target.value)}
                placeholder="Enter text to test against your regex pattern..."
                style={{
                  width: '100%',
                  height: '120px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Common Patterns */}
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px' }}>Common Patterns</h3>
              <div style={{ display: 'grid', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                {commonPatterns.map((patternObj, index) => (
                  <button
                    key={index}
                    onClick={() => loadPattern(patternObj)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '12px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>{patternObj.name}</div>
                    <div style={{ color: '#666', fontSize: '11px' }}>{patternObj.description}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="results-section">
            <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Results</h2>
            
            {/* Match Summary */}
            <div style={{ 
              padding: '15px', 
              backgroundColor: matches.length > 0 ? '#e8f5e8' : '#f5f5f5',
              borderRadius: '8px',
              marginBottom: '20px',
              border: `2px solid ${matches.length > 0 ? '#4caf50' : '#ddd'}`
            }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
                {matches.length > 0 ? `✅ ${matches.length} match${matches.length !== 1 ? 'es' : ''} found` : '❌ No matches found'}
              </div>
              {pattern && testString && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Pattern: <code style={{ backgroundColor: '#f0f0f0', padding: '2px 4px', borderRadius: '3px' }}>
                    /{pattern}/{Object.entries(flags).filter(([_, enabled]) => enabled).map(([flag, _]) => {
                      switch(flag) {
                        case 'global': return 'g';
                        case 'ignoreCase': return 'i';
                        case 'multiline': return 'm';
                        case 'dotAll': return 's';
                        case 'unicode': return 'u';
                        case 'sticky': return 'y';
                        default: return '';
                      }
                    }).join('')}
                  </code>
                </div>
              )}
            </div>

            {/* Highlighted Text */}
            {testString && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1rem', marginBottom: '8px' }}>Highlighted Text:</h3>
                <div style={{
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {highlightMatches(testString)}
                </div>
              </div>
            )}

            {/* Match Details */}
            {matches.length > 0 && (
              <div>
                <h3 style={{ fontSize: '1rem', marginBottom: '10px' }}>Match Details:</h3>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {matches.map((match, index) => (
                    <div 
                      key={index}
                      style={{
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginBottom: '8px',
                        backgroundColor: 'white'
                      }}
                    >
                      <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
                        Match {index + 1}:
                      </div>
                      <div style={{ fontSize: '12px', marginBottom: '3px' }}>
                        <strong>Text:</strong> <code style={{ backgroundColor: '#f0f0f0', padding: '2px 4px', borderRadius: '3px' }}>
                          {match.match}
                        </code>
                      </div>
                      <div style={{ fontSize: '12px', marginBottom: '3px' }}>
                        <strong>Position:</strong> {match.index} - {match.index + match.match.length - 1}
                      </div>
                      {match.groups.length > 0 && (
                        <div style={{ fontSize: '12px' }}>
                          <strong>Groups:</strong> {match.groups.map((group, i) => (
                            <span key={i}>
                              {i > 0 && ', '}
                              <code style={{ backgroundColor: '#f0f0f0', padding: '2px 4px', borderRadius: '3px' }}>
                                ${i + 1}: {group || '(empty)'}
                              </code>
                            </span>
                          ))}
                        </div>
                      )}
                      {Object.keys(match.namedGroups).length > 0 && (
                        <div style={{ fontSize: '12px' }}>
                          <strong>Named Groups:</strong> {Object.entries(match.namedGroups).map(([name, value], i) => (
                            <span key={i}>
                              {i > 0 && ', '}
                              <code style={{ backgroundColor: '#f0f0f0', padding: '2px 4px', borderRadius: '3px' }}>
                                {name}: {value || '(empty)'}
                              </code>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Information Section */}
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>Regex Quick Reference</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Character Classes</h3>
              <div style={{ fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.6' }}>
                <div><code>.</code> - Any character except newline</div>
                <div><code>\d</code> - Digit (0-9)</div>
                <div><code>\w</code> - Word character (a-z, A-Z, 0-9, _)</div>
                <div><code>\s</code> - Whitespace character</div>
                <div><code>[abc]</code> - Any of a, b, or c</div>
                <div><code>[^abc]</code> - Not a, b, or c</div>
              </div>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Quantifiers</h3>
              <div style={{ fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.6' }}>
                <div><code>*</code> - 0 or more</div>
                <div><code>+</code> - 1 or more</div>
                <div><code>?</code> - 0 or 1</div>
                <div><code>{`{3}`}</code> - Exactly 3</div>
                <div><code>{`{3,}`}</code> - 3 or more</div>
                <div><code>{`{3,5}`}</code> - Between 3 and 5</div>
              </div>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Anchors & Groups</h3>
              <div style={{ fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.6' }}>
                <div><code>^</code> - Start of string</div>
                <div><code>$</code> - End of string</div>
                <div><code>\b</code> - Word boundary</div>
                <div><code>(abc)</code> - Capturing group</div>
                <div><code>(?:abc)</code> - Non-capturing group</div>
                <div><code>(?&lt;name&gt;abc)</code> - Named group</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .main-content {
            grid-template-columns: 1fr !important;
            gap: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default RegexTesterPage; 